import React, { Suspense, useEffect } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { P2GBox, P2GGrid, P2GLink, P2GProgress, P2GStack, P2GText, useP2GMediaQueryDown } from '@parcel2go/core-ui';
import { Order, Service, TrackingDetails, TrackingEvent } from '~/lib/types/tracking-types';
import ExternalTrackingLink from '../external';
import { mapParcelStage, translatedTrackingStage } from '../utils/mapParcelStage';
import { TranslationNamespaces } from '~/lib/constants';

const TrackingEventsTable = React.lazy(() =>
  import('../table').then((module) => ({ default: module.TrackingEventsTable }))
);

const TrackingMap = React.lazy(() => import('../map'));
const TrackingImages = React.lazy(() => import('../images'));

export const ParcelTitle: React.FC<IParcelProps> = (props) => {
  const { t } = useTranslation(TranslationNamespaces.TRACKING);

  function truncate(value: string | null) {
    if (value && value.length > 50) {
      return `${value.slice(0, 50)}...`;
    }
    return value;
  }

  const stage = React.useMemo(() => {
    return mapParcelStage(props.parcel);
  }, [props.parcel]);

  const estimatedDeliveryDate = React.useMemo(() => {
    const date = new Date(props.order?.estimatedDeliveryDate || 0);

    return date;
  }, [props.order]);

  return (
    <P2GBox>
      <P2GStack direction="row" spacing={3}>
        {props.parcel.currentStage && (
          <P2GGrid>
            <Trans t={t} i18nKey="HISTORY_PARCEL_TITLE_CURRENT_STATUS">
              Current status
            </Trans>
            {': '}
            {translatedTrackingStage(t, stage)}
          </P2GGrid>
        )}
        {props.parcel.parcel.contents && (
          <P2GGrid>
            <P2GText small>{truncate(props.parcel.parcel.contents)}</P2GText>
          </P2GGrid>
        )}
        {props.order?.estimatedDeliveryDate && (
          <P2GGrid>
            <Trans t={t} i18nKey="HISTORY_PARCEL_TITLE_ETA">
              {/* @ts-expect-error object literal not expected as part of the children prop*/}
              Estimated Delivery Date: <strong>{{ date: estimatedDeliveryDate.toDateString() }}</strong>
            </Trans>
          </P2GGrid>
        )}
      </P2GStack>
    </P2GBox>
  );
};

interface IParcelDeliveryDetailsProps {
  hasImages: boolean | null;
  withGeo?: TrackingEvent | null;
  parcel: TrackingDetails;
}

export const ParcelDeliveryDetails: React.FC<IParcelDeliveryDetailsProps> = (props) => {
  const isBreak = useP2GMediaQueryDown('sm');

  return (
    <P2GGrid container wrap={isBreak ? 'wrap' : 'nowrap'} spacing={2} direction={isBreak ? 'column' : 'row'}>
      {props.parcel.images && props.parcel.images.length > 0 && (
        <P2GGrid item>
          <TrackingImages images={props.parcel.images} />
        </P2GGrid>
      )}

      {props.withGeo && props.withGeo.latitude && props.withGeo.longitude && (
        <P2GGrid item flexGrow={1}>
          <TrackingMap latitude={props.withGeo.latitude} longitude={props.withGeo.longitude} />
        </P2GGrid>
      )}
    </P2GGrid>
  );
};

export const ParcelItem: React.FC<IParcelProps> = (props) => {
  const { t } = useTranslation(TranslationNamespaces.TRACKING);
  const [showAllTrackingEvents, setShowAllTrackingEvents] = React.useState<boolean>(false);
  const [visibleParcelEvents, setVisibleParcelEvents] = React.useState<TrackingEvent[]>(
    props.parcel.events.slice(0, 5)
  );

  const hasImages = React.useMemo(() => {
    return props.parcel && props.parcel.images && props.parcel.images.length > 0;
  }, [props.parcel]);

  const withGeo = React.useMemo(() => {
    if (props.parcel && props.parcel.events) {
      return props.parcel.events.filter((x) => x.latitude && x.longitude && x.stage === 'Delivered')[0];
    }
    return null;
  }, [props.parcel]);

  const handleShowMore = React.useCallback(
    (events: TrackingEvent[]) => {
      if (events === visibleParcelEvents) {
        setShowAllTrackingEvents(false);
        setVisibleParcelEvents(events.slice(0, 5));
        return;
      }
      setShowAllTrackingEvents(true);
      setVisibleParcelEvents(events);
      return;
    },
    [visibleParcelEvents]
  );

  useEffect(() => {
    setShowAllTrackingEvents(false);
    setVisibleParcelEvents(props.parcel.events.slice(0, 5));
  }, [props.parcel]);

  return (
    <P2GStack direction="column" gap={2}>
      <ParcelTitle
        parcel={props.parcel}
        order={props.order}
        service={props.service}
        parcelNumber={props.parcelNumber}
        multiParcels={props.multiParcels}
        metadata={props.metadata}
      />
      {(hasImages || withGeo) && (
        <ParcelDeliveryDetails hasImages={hasImages} withGeo={withGeo} parcel={props.parcel} />
      )}
      <Suspense fallback={<P2GProgress />}>
        <TrackingEventsTable events={visibleParcelEvents} parcelNumber={props.parcelNumber} />
      </Suspense>
      <Suspense fallback={<P2GProgress />}>
        <P2GGrid
          container={true}
          alignItems="center"
          justify={props.metadata && props.metadata['ExternalTrackingUrl'] ? 'space-between' : 'flex-end'}
          direction="row"
          spacing={2}
        >
          {props.metadata && props.metadata['ExternalTrackingUrl'] && (
            <ExternalTrackingLink
              link={props.metadata['ExternalTrackingUrl']}
              trackingNumber={props.parcelNumber}
              postcode={props.order?.deliveryPostcode}
              keyEvents={props.parcel.keyEvents}
              showMessage={false}
              sectionStyle="trackingTable"
              parcelReferences={props.parcel.parcel.parcelReferences}
            />
          )}
          {props.parcel.events.length > 5 && (
            <P2GGrid item={true}>
              <P2GLink onClick={() => handleShowMore(props.parcel.events)}>
                {showAllTrackingEvents ? (
                  <Trans t={t} i18nKey="HISTORY_PARCEL_SHOW_LESS">
                    Show Less
                  </Trans>
                ) : (
                  <Trans t={t} i18nKey="HISTORY_PARCEL_SHOW_MORE">
                    Show More
                  </Trans>
                )}
              </P2GLink>
            </P2GGrid>
          )}
        </P2GGrid>
      </Suspense>
    </P2GStack>
  );
};

interface IParcelProps {
  parcelNumber: string;
  parcel: TrackingDetails;
  service: Service;
  multiParcels?: boolean;
  metadata?: { [name: string]: string } | null;
  order?: Order | null;
}

export const Parcel: React.FC<IParcelProps> = (props) => {
  return (
    <P2GStack container direction="column" gap={1}>
      {props.multiParcels && <ParcelItem {...props} />}
      {!props.multiParcels && <ParcelItem {...props} />}
    </P2GStack>
  );
};
